import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  MobileStepper,
  useMediaQuery,
  TextField
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AddIcon from "@mui/icons-material/Add";

const clientMongoId = localStorage.getItem("clientMongoId");

function Price() {
  const [priceData, setPriceData] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const isMobile = useMediaQuery("(max-width:600px)");

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editImg, setEditImg] = useState("");
  const [editPriceRange, setEditPriceRange] = useState("");

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteIdx, setDeleteIdx] = useState(null);

  // Edit package dialog state
  const [editPackageDialogOpen, setEditPackageDialogOpen] = useState(false);
  const [editPackageModel, setEditPackageModel] = useState("");
  const [editPackageAmount, setEditPackageAmount] = useState("");
  const [editPackagePhotographer, setEditPackagePhotographer] = useState("");
  const [editPackageCamera, setEditPackageCamera] = useState("");
  const [editPackageTime, setEditPackageTime] = useState("");
  const [editPackageVideographer, setEditPackageVideographer] = useState("");
  const [editPackageDetails, setEditPackageDetails] = useState("");
  const [editPackageImg, setEditPackageImg] = useState(""); // State for package image

  useEffect(() => {
    async function fetchPrices() {
      if (!clientMongoId) return;
      try {
        const res = await axios.get(`https://studio-lc.onrender.com/api/clients/${clientMongoId}/prices`);
        setPriceData(res.data.prices && res.data.prices.length > 0 ? res.data.prices : []);
      } catch (err) {
        setPriceData([]);
      }
    }
    fetchPrices();
  }, [clientMongoId]);

  const savePricesToDB = async (prices) => {
    try {
      await axios.put(`https://studio-lc.onrender.com/api/clients/${clientMongoId}/prices`, {
        prices
      });
    } catch (err) {
      alert("Failed to update prices.");
    }
  };

  const handleCardClick = (idx) => {
    setSelectedIdx(idx);
    setActiveStep(0);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedIdx(null);
    setActiveStep(0);
  };

  const handleNext = () => {
    if (selectedIdx !== null) {
      setActiveStep((prev) => Math.min(prev + 1, priceData[selectedIdx].packages.length - 1));
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  // Edit functions
  const handleEditClick = (idx) => {
    setEditTitle(priceData[idx].title);
    setEditImg(priceData[idx].img);
    setEditPriceRange(priceData[idx].priceRange);
    setSelectedIdx(idx);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    const updated = [...priceData];
    updated[selectedIdx] = {
      ...updated[selectedIdx],
      title: editTitle,
      img: editImg,
      priceRange: editPriceRange
    };
    setPriceData(updated);
    setEditDialogOpen(false);
    await savePricesToDB(updated);
  };

  const handlePackageEditSave = async () => {
    const updated = [...priceData];
    updated[selectedIdx].packages[activeStep] = {
      model: editPackageModel,
      amount: editPackageAmount,
      photographer: editPackagePhotographer,
      camera: editPackageCamera,
      time: editPackageTime,
      videographer: editPackageVideographer,
      details: editPackageDetails,
      img: editPackageImg
    };
    setPriceData(updated);
    setEditPackageDialogOpen(false);
    await savePricesToDB(updated);
  };

  // Delete functions
  const handleDeleteClick = (idx) => {
    setDeleteIdx(idx);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    const updated = priceData.filter((_, i) => i !== deleteIdx);
    setPriceData(updated);
    setDeleteDialogOpen(false);
    if (selectedIdx === deleteIdx) handleClose();
  };

  return (
    <Box sx={{ pt: 2, px: isMobile ? 1 : 6, bgcolor: "#f9f7f3", minHeight: "100vh" }}>
      <Box
        sx={{
          bgcolor: "#2492ffff",
          borderRadius: 4,
          py: 5,
          mb: 4,
          boxShadow: 2,
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            color: "#fff",
            textAlign: "center",
            fontFamily:"Josefin Sans",
            letterSpacing: 2,
            mb: 1,
          }}
        >
          Price List
        </Typography>
        <Typography
          variant="h6"
          sx={{
            textAlign: "center",
            color: "#fff",
            fontFamily: "jost",
            fontWeight: 400,
            mb: 2,
          }}
        >
          Explore our packages and find the perfect fit for your special day.
        </Typography>
      </Box>
      <Grid container spacing={4} justifyContent="center">
        {priceData.map((item, idx) => (
          <Grid item xs={12} sm={6} md={4} key={item.title}>
            <Card
              sx={{
                borderRadius: 4,
                boxShadow: 4,
                width: isMobile ? 320 : 340,
                height: isMobile ? 370 : 410,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "stretch",
                position: "relative",
                transition: "transform 0.3s",
                background: "linear-gradient(120deg,#fff 60%,#e7dfd5 100%)",
                "&:hover": { transform: "scale(1.04)", boxShadow: 8, background: "linear-gradient(120deg,#e3f2fd 60%,#e7dfd5 100%)" }
              }}
            >
              {/* Image always at the top */}
              <Box sx={{ width: "100%", height: isMobile ? 140 : 180, overflow: "hidden", borderTopLeftRadius: 16, borderTopRightRadius: 16 }}>
                <img
                  src={item.img}
                  alt={item.title}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16
                  }}
                  onClick={() => handleCardClick(idx)}
                />
              </Box>
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 2,
                  cursor: "pointer"
                }}
                onClick={() => handleCardClick(idx)}
              >
                <Typography
                  gutterBottom
                  variant="h5"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    color: "#8b5e3c",
                    textAlign: "center",
                    fontFamily:"Josefin Sans",
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    textAlign: "center",
                    fontWeight: 700,
                    color: "#1976d2",
                    fontFamily:"Josefin Sans",
                    mb: 1
                  }}
                >
                  {item.priceRange}
                </Typography>
              </CardContent>
              {/* Edit and Delete icons below content */}
              <Box sx={{ display: "flex", justifyContent: "center", gap: 2, pb: 2 }}>
                <IconButton
                  size="small"
                  sx={{ bgcolor: "#fff", boxShadow: 1, "&:hover": { bgcolor: "#e3f2fd" } }}
                  onClick={async () => {
                    const updated = [...priceData];
                    updated.splice(idx + 1, 0, { ...item });
                    setPriceData(updated);
                    await savePricesToDB(updated);
                  }}
                >
                  <AddIcon fontSize="small" sx={{ color: "#1976d2" }} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ bgcolor: "#fff", boxShadow: 1, "&:hover": { bgcolor: "#e3f2fd" } }}
                  onClick={() => handleEditClick(idx)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ bgcolor: "#fff", boxShadow: 1, "&:hover": { bgcolor: "#fdecea" } }}
                  onClick={() => handleDeleteClick(idx)}
                >
                  <DeleteIcon fontSize="small" sx={{ color: "#d32f2f" }} />
                </IconButton>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
      {/* Price Details Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: 8,
            bgcolor: "#fff"
          }
        }}
      >
        {selectedIdx !== null && (
          <>
            {/* Image at the top */}
            <Box sx={{ width: "100%", height: 180, overflow: "hidden", borderTopLeftRadius: 16, borderTopRightRadius: 16 }}>
              <img
                src={priceData[selectedIdx].img}
                alt={priceData[selectedIdx].title}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderTopLeftRadius: 16,
                  borderTopRightRadius: 16
                }}
              />
            </Box>
            <DialogTitle
              sx={{
                fontWeight: 700,
                color: "#8b5e3c",
                textAlign: "center",
                fontSize: 28,
              fontFamily:"Josefin Sans",
                letterSpacing: 2
              }}
            >
              {priceData[selectedIdx].title} Packages
            </DialogTitle>
            <DialogContent>
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#1976d2", fontFamily: "jost" }}>
                  {priceData[selectedIdx].packages[activeStep].model}
                </Typography>
                <Typography variant="h6" sx={{ color: "#8b5e3c", fontWeight: 700, fontFamily:"Josefin Sans", }}>
                  {priceData[selectedIdx].packages[activeStep].amount}
                </Typography>
              </Box>
              <Box sx={{ position: "relative", mb: 2 }}>
                {/* + Icon before Edit */}
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 88,
                    bgcolor: "#fff",
                    boxShadow: 1,
                    "&:hover": { bgcolor: "#e3f2fd" }
                  }}
                  onClick={async () => {
                    const updated = [...priceData];
                    const pkg = updated[selectedIdx].packages[activeStep];
                    updated[selectedIdx].packages.splice(activeStep + 1, 0, { ...pkg });
                    setPriceData(updated);
                    await savePricesToDB(updated);
                  }}
                >
                  <AddIcon fontSize="small" sx={{ color: "#1976d2" }} />
                </IconButton>
                {/* Edit button */}
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 44,
                    bgcolor: "#fff",
                    boxShadow: 1,
                    "&:hover": { bgcolor: "#e3f2fd" }
                  }}
                  onClick={() => {
                    const pkg = priceData[selectedIdx].packages[activeStep];
                    setEditPackageModel(pkg.model);
                    setEditPackageAmount(pkg.amount);
                    setEditPackagePhotographer(pkg.photographer);
                    setEditPackageCamera(pkg.camera);
                    setEditPackageTime(pkg.time);
                    setEditPackageVideographer(pkg.videographer);
                    setEditPackageDetails(pkg.details);
                    setEditPackageImg(pkg.img || "");
                    setEditPackageDialogOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                {/* Delete button */}
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bgcolor: "#fff",
                    boxShadow: 1,
                    "&:hover": { bgcolor: "#fdecea" }
                  }}
                  onClick={async () => {
                    // Remove the current package
                    const updated = [...priceData];
                    updated[selectedIdx].packages.splice(activeStep, 1);
                    // If no packages left, close dialog
                    if (updated[selectedIdx].packages.length === 0) {
                      setOpen(false);
                    } else if (activeStep >= updated[selectedIdx].packages.length) {
                      setActiveStep(updated[selectedIdx].packages.length - 1);
                    }
                    setPriceData(updated);
                    await savePricesToDB(updated);
                  }}
                  disabled={priceData[selectedIdx].packages.length === 1}
                >
                  <DeleteIcon fontSize="small" sx={{ color: "#d32f2f" }} />
                </IconButton>
                <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: "jost" }}>
                  Photographer: <span style={{ fontWeight: 400 }}>{priceData[selectedIdx].packages[activeStep].photographer}</span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: "jost" }}>
                  Camera/Resolution: <span style={{ fontWeight: 400 }}>{priceData[selectedIdx].packages[activeStep].camera}</span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: "jost" }}>
                  Time: <span style={{ fontWeight: 400 }}>{priceData[selectedIdx].packages[activeStep].time}</span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: "jost" }}>
                  Videographer: <span style={{ fontWeight: 400 }}>{priceData[selectedIdx].packages[activeStep].videographer}</span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: "jost" }}>
                  Other Details: <span style={{ fontWeight: 400 }}>{priceData[selectedIdx].packages[activeStep].details}</span>
                </Typography>
              </Box>
              <MobileStepper
                variant="dots"
                steps={priceData[selectedIdx].packages.length}
                position="static"
                activeStep={activeStep}
                nextButton={
                  <IconButton
                    onClick={handleNext}
                    disabled={activeStep === priceData[selectedIdx].packages.length - 1}
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>
                }
                backButton={
                  <IconButton
                    onClick={handleBack}
                    disabled={activeStep === 0}
                  >
                    <ArrowBackIosNewIcon />
                  </IconButton>
                }
                sx={{ justifyContent: "center", mt: 2 }}
              />
              <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
                <IconButton
                  size="small"
                  sx={{ bgcolor: "#fff", boxShadow: 1, "&:hover": { bgcolor: "#e3f2fd" } }}
                  onClick={() => {
                    setEditTitle(priceData[selectedIdx].title);
                    setEditImg(priceData[selectedIdx].img);
                    setEditPriceRange(priceData[selectedIdx].priceRange);
                    setEditDialogOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ bgcolor: "#fff", boxShadow: 1, "&:hover": { bgcolor: "#fdecea" } }}
                  onClick={() => handleDeleteClick(selectedIdx)}
                >
                  <DeleteIcon fontSize="small" sx={{ color: "#d32f2f" }} />
                </IconButton>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button sx={{fontFamily:"Josefin Sans",}} onClick={handleClose}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      {/* Edit Category Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
            <img
              src={editImg}
              alt="Category"
              style={{
                width: 180,
                height: 120,
                objectFit: "cover",
                borderRadius: 8,
                marginBottom: 8,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(25,118,210,0.10)"
              }}
              onClick={() => document.getElementById("edit-image-input").click()}
            />
            <input
              id="edit-image-input"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={async (e) => {
                const file = e.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (ev) => setEditImg(ev.target.result);
                  reader.readAsDataURL(file);
                }
              }}
            />
            <Typography variant="caption" sx={{ color: "#1976d2", cursor: "pointer" }} onClick={() => document.getElementById("edit-image-input").click()}>
              Click image to change
            </Typography>
          </Box>
          <TextField
            label="Title"
            fullWidth
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Price Range"
            fullWidth
            value={editPriceRange}
            onChange={e => setEditPriceRange(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button sx={{fontFamily:"Josefin Sans",}} onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button sx={{fontFamily:"Josefin Sans",}} variant="contained" onClick={handleEditSave}>Save</Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{fontFamily:"Josefin Sans",}}>Are you sure you want to delete this category?</DialogTitle>
        <DialogActions>
      <Button sx={{fontFamily:"Josefin Sans",}} onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
      <Button sx={{fontFamily:"Josefin Sans",}} variant="contained" color="error" onClick={handleDeleteConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>
      {/* Edit Package Details Dialog */}
      <Dialog open={editPackageDialogOpen} onClose={() => setEditPackageDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Package</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
            <img
              src={editPackageImg || "https://via.placeholder.com/180x120?text=Package"}
              alt="Package"
              style={{
                width: 180,
                height: 120,
                objectFit: "cover",
                borderRadius: 8,
                marginBottom: 8,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(25,118,210,0.10)"
              }}
              onClick={() => document.getElementById("edit-package-image-input").click()}
            />
            <input
              id="edit-package-image-input"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={async (e) => {
                const file = e.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (ev) => setEditPackageImg(ev.target.result);
                  reader.readAsDataURL(file);
                }
              }}
            />
            <Typography variant="caption" sx={{ color: "#1976d2", cursor: "pointer" }} onClick={() => document.getElementById("edit-package-image-input").click()}>
              Click image to change
            </Typography>
          </Box>
          <TextField
            label="Model"
            fullWidth
            value={editPackageModel}
            onChange={e => setEditPackageModel(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            label="Amount"
            fullWidth
            value={editPackageAmount}
            onChange={e => setEditPackageAmount(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            label="Photographer"
            fullWidth
            value={editPackagePhotographer}
            onChange={e => setEditPackagePhotographer(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            label="Camera/Resolution"
            fullWidth
            value={editPackageCamera}
            onChange={e => setEditPackageCamera(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            label="Time"
            fullWidth
            value={editPackageTime}
            onChange={e => setEditPackageTime(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            label="Videographer"
            fullWidth
            value={editPackageVideographer}
            onChange={e => setEditPackageVideographer(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            label="Details"
            fullWidth
            multiline
            minRows={3}
            value={editPackageDetails}
            onChange={e => setEditPackageDetails(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button sx={{fontFamily:"Josefin Sans",}} onClick={() => setEditPackageDialogOpen(false)}>Cancel</Button>
          <Button sx={{fontFamily:"Josefin Sans",}}
            variant="contained"
            onClick={handlePackageEditSave}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Price;